import { io, Socket } from "socket.io-client";
import { logger } from "@/core";
import type {
  SocketConnectionPayload,
  SocketExtractionProgressPayload,
  SocketExtractionCompletePayload,
} from "../types";

type ExtractionProgressCallback = (
  progress: SocketExtractionProgressPayload,
) => void;
type ExtractionCompleteCallback = (
  result: SocketExtractionCompletePayload,
) => void;
type ErrorCallback = (error: Error) => void;

interface SocketServiceOptions {
  url?: string;
  timeout?: number;
}

/**
 * Socket service
 *
 * why: Manage websocket connnection for realtime progress updates
 * What: Handles socket lifecycle and event subscriptions
 *
 * Benefits:
 *      - Centralized socket managment
 *      - Clean connection/disconnection
 *      - Type-safe event handling
 */

class SocketService {
  private socket: Socket | null = null;
  private socketId: string | null = null;
  private readonly defaultUrl: string;
  private readonly defaultTimeout: number;

  constructor(options: SocketServiceOptions = {}) {
    this.defaultUrl = import.meta.env.VITE_API_BASE_SOCKET_URL;
    this.defaultTimeout = options.timeout || 10000;
  }

  /**
   * Connect to socket server
   * Returns socketId for use in API requests
   */
  async connect(): Promise<string> {
    if (this.socket?.connected && this.socketId) {
      logger.debug(`Socket already connected`, { socketId: this.socketId });
      return this.socketId;
    }
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.disconnect();
        reject(new Error("Socket connection timeout"));
      }, this.defaultTimeout);

      this.socket = io(this.defaultUrl, {
        transports: ["websocket"],
        reconnection: true,
        reconnectionAttempts: 3,
        reconnectionDelay: 1000,
      });

      this.socket.on("connected", (payload: SocketConnectionPayload) => {
        clearTimeout(timeout);
        this.socketId = payload.socketId;
        logger.info(`Socket connected`, { socketId: this.socketId });
        resolve(this.socketId);
      });
      this.socket.on("connect_error", (error) => {
        clearTimeout(timeout);
        logger.error(`Socket connection error`, error);
        reject(error);
      });
    });
  }
  /**
   * Disconnect fro socket server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.socketId = null;
      logger.info("Socket disconnected");
    }
  }
  /**
   *  Get current socket ID
   */
  getSocketId(): string | null {
    return this.socketId;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Subscribe to extraction progress
   */
  onExtractionWebFileProgress(
    callback: ExtractionProgressCallback,
  ): () => void {
    if (!this.socket) {
      logger.warn(
        `Cannot subscribe to extraction progress: socket not connected`,
      );
      return () => {};
    }

    this.socket.on("webFileExtractionProgress", callback);

    // Return unsubscribe function
    return () => {
      this.socket?.off("webFileExtractionProgress", callback);
    };
  }
  /**
   * Subscribe to extraction complete
   */
  onExtractionWebFileComplete(
    callback: ExtractionCompleteCallback,
  ): () => void {
    if (!this.socket) {
      logger.warn(
        `Cannot subscribe to extraction complete: socket not connected`,
      );
      return () => {};
    }
    this.socket.on("webFileExtractionComplete", callback);

    return () => {
      this.socket?.off("webFileExtractionComplete", callback);
    };
  }

  /**
   * Subscribe to errors
   */
  onError(callback: ErrorCallback): () => void {
    if (!this.socket) {
      return () => {};
    }
    const handler = (data: { message: string }) => {
      callback(new Error(data.message));
    };
    this.socket.on("error", handler);
    return () => {
      this.socket?.off("error", handler);
    };
  }
  /**
   * Emit cancel upload event
   */
  emitCancelUpload(fileId: string): void {
    if (this.socket?.connected) {
      this.socket.emit("cancelUpload", { fileId });
    }
  }
}
// Export singleton instance
export const socketService = new SocketService();
